// Product page enhancements (progressive; the page works without this file):
// variant picker, price/availability updates, ?variant= URL, gallery thumbnails and
// variant images, lightbox and size-guide dialogs, quantity steppers.
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatMoney(cents, format) {
    const value = (Number(cents) / 100);
    const withDelimiters = (number, decimals, thousands, decimal) => {
      const fixed = number.toFixed(decimals).split('.');
      fixed[0] = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fixed.join(decimals ? decimal : '');
    };
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        default: return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  // ---------------------------------------------------------------- dialogs
  let lastOpener = null;
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      if (dialog && dialog.showModal) {
        lastOpener = opener;
        dialog.showModal();
      }
      return;
    }
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) closer.closest('dialog').close();
  });
  document.querySelectorAll('[data-dialog-open]').forEach((el) => { el.hidden = false; });
  document.querySelectorAll('dialog').forEach((dialog) => {
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => {
      if (lastOpener && document.contains(lastOpener)) lastOpener.focus();
      lastOpener = null;
    });
  });

  // Review summary links to the review app's section; drop the link if that section isn't on the page.
  document.querySelectorAll('[data-review-link]').forEach((link) => {
    const target = link.getAttribute('href');
    if (target && target.startsWith('#') && document.getElementById(target.slice(1))) return;
    const summary = document.createElement('span');
    summary.className = link.className;
    summary.append(...link.childNodes);
    link.replaceWith(summary);
  });
  // Scroll with the theme's header offset without changing the URL hash (Judge.me runs its own
  // scroll when the hash is #judgeme_product_reviews, which lands under the sticky header).
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-review-link]');
    const target = link && document.getElementById(link.getAttribute('href').slice(1));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  document.querySelectorAll('[data-product]').forEach((root) => {
    const labels = root.querySelector('template[data-labels]')?.dataset || {};

    // -------------------------------------------------------------- gallery
    const gallery = root.querySelector('[data-gallery]');
    const track = gallery?.querySelector('[data-gallery-track]');
    const slides = track ? [...track.children] : [];
    const thumbs = gallery ? [...gallery.querySelectorAll('[data-gallery-thumb]')] : [];
    const counter = gallery?.querySelector('[data-gallery-current]');
    let current = 0;

    // Roving tab order: only the visible image link and the current thumbnail are Tab stops;
    // arrow keys (and Home/End) move between thumbnails.
    const setCurrent = (index) => {
      current = index;
      thumbs.forEach((t, i) => {
        t.setAttribute('aria-current', i === index ? 'true' : 'false');
        t.tabIndex = i === index ? 0 : -1;
      });
      slides.forEach((s, i) => {
        const link = s.querySelector('[data-lightbox-open]');
        if (link) link.tabIndex = i === index ? 0 : -1;
      });
      if (counter) counter.textContent = String(index + 1);
    };
    const goTo = (index) => {
      const slide = slides[index];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      setCurrent(index);
    };
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.galleryThumb))));
    thumbs.forEach((thumb, i) => thumb.addEventListener('keydown', (event) => {
      const last = thumbs.length - 1;
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = i === last ? 0 : i + 1;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = i === 0 ? last : i - 1;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = last;
      if (next === null) return;
      event.preventDefault();
      goTo(next);
      thumbs[next].focus();
    }));
    if (slides.length) setCurrent(0);
    if (track) {
      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
          if (index !== current) setCurrent(Math.min(Math.max(index, 0), slides.length - 1));
          ticking = false;
        });
      });
      const start = gallery.querySelector('[data-gallery-start]')?.dataset.galleryStart;
      const startIndex = slides.findIndex((s) => s.dataset.mediaId === start);
      if (startIndex > 0) {
        track.scrollLeft = slides[startIndex].offsetLeft - track.offsetLeft;
        setCurrent(startIndex);
      }
    }
    const showMedia = (mediaId) => {
      const index = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (index >= 0) goTo(index);
    };

    // -------------------------------------------------------------- lightbox
    const lightbox = gallery?.querySelector('[data-lightbox]');
    if (lightbox && lightbox.showModal) {
      const image = lightbox.querySelector('[data-lightbox-image]');
      const lbCounter = lightbox.querySelector('[data-lightbox-counter]');
      let lbIndex = 0;
      const render = (index) => {
        lbIndex = (index + slides.length) % slides.length;
        const link = slides[lbIndex].querySelector('[data-lightbox-open]');
        const source = slides[lbIndex].querySelector('img');
        image.src = link.href;
        image.alt = source ? source.alt : '';
        if (lbCounter) lbCounter.textContent = `${lbIndex + 1} / ${slides.length}`;
      };
      slides.forEach((slide, index) => {
        slide.querySelector('[data-lightbox-open]')?.addEventListener('click', (event) => {
          event.preventDefault();
          lastOpener = event.currentTarget;
          render(index);
          lightbox.showModal();
        });
      });
      lightbox.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => render(lbIndex - 1));
      lightbox.querySelector('[data-lightbox-next]')?.addEventListener('click', () => render(lbIndex + 1));
      lightbox.addEventListener('keydown', (event) => {
        if (slides.length < 2) return;
        if (event.key === 'ArrowLeft') render(lbIndex - 1);
        if (event.key === 'ArrowRight') render(lbIndex + 1);
      });
      lightbox.addEventListener('close', () => goTo(lbIndex));
    }

    // -------------------------------------------------------------- quantity
    root.querySelectorAll('[data-quantity]').forEach((wrapper) => {
      const input = wrapper.querySelector('input');
      wrapper.querySelectorAll('[data-quantity-step]').forEach((button) => {
        button.hidden = false;
        button.addEventListener('click', () => {
          const next = Math.max(Number(input.min) || 1, (Number(input.value) || 1) + Number(button.dataset.quantityStep));
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });

    // -------------------------------------------------------------- variants
    const picker = root.querySelector('[data-variant-picker]');
    if (!picker) return;
    const variants = JSON.parse(picker.querySelector('[data-variant-json]').textContent);
    const select = picker.querySelector('[data-variant-select]');
    const options = picker.querySelector('[data-variant-options]');
    const fieldsets = [...picker.querySelectorAll('[data-option-index]')];
    const submit = root.querySelector('[data-add-to-cart]');
    const submitLabel = root.querySelector('[data-add-to-cart-label]');
    const priceRegion = root.querySelector('[data-price-region]');
    const status = root.querySelector('[data-variant-status]');

    options.hidden = false;
    picker.querySelector('[data-variant-fallback]').hidden = true;

    const selectedValues = () => fieldsets.map((fs) => fs.querySelector('[data-option-value]:checked')?.value);
    const findVariant = (values) => variants.find((v) => v.options.every((o, i) => o === values[i]));

    const renderPrice = (variant) => {
      if (!priceRegion) return;
      const price = formatMoney(variant.price, labels.moneyFormat);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      priceRegion.innerHTML = onSale
        ? `<div class="price price--sale"><span class="visually-hidden">${labels.salePrice}</span><span class="price__current">${price}</span><span class="visually-hidden">${labels.regularPrice}</span><s class="price__compare">${formatMoney(variant.compare_at_price, labels.moneyFormat)}</s><span class="price__label">${labels.sale}</span></div>`
        : `<div class="price"><span class="price__current">${price}</span></div>`;
    };

    const refreshStates = (values) => {
      fieldsets.forEach((fs, index) => {
        fs.querySelectorAll('[data-option-value]').forEach((input) => {
          const candidate = values.slice();
          candidate[index] = input.value;
          const match = findVariant(candidate);
          const label = input.nextElementSibling;
          const state = label.querySelector('[data-value-state]');
          label.classList.toggle('is-missing', !match);
          label.classList.toggle('is-unavailable', !match || !match.available);
          state.innerHTML = !match
            ? `<span class="visually-hidden">, ${labels.unavailable}</span>`
            : (!match.available ? `<span class="visually-hidden">, ${labels.soldOut}</span>` : '');
        });
        const selected = fs.querySelector('[data-option-selected]');
        if (selected) selected.textContent = values[index] || '';
      });
    };

    const update = () => {
      const values = selectedValues();
      const variant = findVariant(values);
      refreshStates(values);
      if (!variant) {
        submit.disabled = true;
        submitLabel.textContent = labels.unavailable;
        if (status) status.textContent = `${values.join(' / ')}, ${labels.unavailable}`;
        return;
      }
      select.value = String(variant.id);
      submit.disabled = !variant.available;
      submitLabel.textContent = variant.available ? labels.add : labels.soldOut;
      renderPrice(variant);
      if (variant.featured_media) showMedia(variant.featured_media.id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      if (status) status.textContent = `${variant.title}, ${formatMoney(variant.price, labels.moneyFormat)}, ${variant.available ? labels.available : labels.soldOut}`;
    };

    picker.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-value]')) update();
    });
  });
})();
